import { useRef, useSyncExternalStore } from "react"
import { createFrameNotifier } from "./createFrameNotifier"

const isShallowEqual = <T>(a: T[], b: T[]) =>
    a.length === b.length && a.every((item, i) => item === b[i])

export const createExternalStore = <T, K = string>({
    maxEntries,
}: { maxEntries?: number } = {}) => {
    const items = new Map<K, T>()
    const { notify, subscribe } = createFrameNotifier()
    let version = 0
    let snapshot = { version, items: new Map(items) as ReadonlyMap<K, T> }

    const commit = () => {
        version++
        notify()
    }

    const set = (id: K, value: T) => {
        items.set(id, value)
        commit()
    }

    const prune = (protectedIds: Iterable<K>) => {
        if (maxEntries == null || items.size <= maxEntries) return
        const protectedSet = new Set(protectedIds)
        const sizeBefore = items.size
        for (const id of items.keys()) {
            if (items.size <= maxEntries) break
            if (!protectedSet.has(id)) items.delete(id)
        }
        if (items.size < sizeBefore) commit()
    }

    const replaceAll = (values: T[], keyFn: (value: T) => K) => {
        items.clear()
        for (const value of values) items.set(keyFn(value), value)
        commit()
    }

    const remove = (id: K) => {
        if (items.delete(id)) commit()
    }

    const flush = () => {
        items.clear()
        commit()
    }

    const get = () => {
        if (snapshot.version !== version) snapshot = { version, items: new Map(items) }
        return snapshot.items
    }
    const getItem = (id: K) => items.get(id)
    const getItems = (ids: K[]) =>
        ids.map((id) => items.get(id)).filter((item): item is T => !!item)

    const useAll = () => useSyncExternalStore(subscribe, get)
    const useItem = (id: K) => useSyncExternalStore(subscribe, () => getItem(id))
    const useItems = (ids: K[]) => {
        const previous = useRef<T[]>([])
        return useSyncExternalStore(subscribe, () => {
            const next = getItems(ids)
            if (!isShallowEqual(previous.current, next)) previous.current = next
            return previous.current
        })
    }

    return {
        set,
        prune,
        replaceAll,
        remove,
        flush,
        get,
        getItem,
        getItems,
        useAll,
        useItem,
        useItems,
    }
}
